/**
 * Token utility functions for local JWT validation
 * Story 4.9: Used by ProtectedRoute to check token expiry without API calls
 */

/**
 * Get refresh threshold from environment variable
 * Uses VITE_SESSION_REFRESH_THRESHOLD_MINUTES (default: 300 minutes = 5 hours)
 */
function getRefreshThresholdMs(): number {
  const minutes = parseInt(import.meta.env.VITE_SESSION_REFRESH_THRESHOLD_MINUTES, 10) || 300;
  return minutes * 60 * 1000;
}

/**
 * Decode JWT payload without validation (for expiry check only)
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJwtPayload(token: string | null): { exp?: number } | null {
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

/**
 * Check if token needs refresh (expired or expiring within threshold)
 * @param token JWT token string
 * @param thresholdMs Optional override for refresh threshold in milliseconds
 *                    Defaults to VITE_SESSION_REFRESH_THRESHOLD_MINUTES env var
 * @returns true if token needs refresh, false if still valid
 */
export function tokenNeedsRefresh(
  token: string | null,
  thresholdMs?: number
): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true; // No token or no exp = needs refresh

  const threshold = thresholdMs ?? getRefreshThresholdMs();
  const expiresAtMs = payload.exp * 1000; // Convert to ms
  const now = Date.now();

  // Token needs refresh if expired OR expiring within threshold
  return expiresAtMs - now < threshold;
}
